import {
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Divider,
  Switch,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ViewListIcon from "@mui/icons-material/ViewList";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import axios from "axios";
import { useNavigate, Link as RouterLink, Routes, Route, Navigate, useLocation } from "react-router-dom";
import CreateJob from "./createJob";
import WebsiteBrandingPanel from "./WebsiteBrandingPanel";
import OperatorQuoteLinkPanel from "./OperatorQuoteLinkPanel";
import BillingPanel from "./BillingPanel";
import { DataGrid } from "@mui/x-data-grid";
import { useAppGlass } from "./glass";
import { quoteServiceDisplayPrice } from "./quotePricing";
import { getAuthHeaders, getRole, setRole, clearAuth } from "./auth";
import { useColorMode } from "./themeMode";
import { useCatalogServices } from "./useCatalogServices";
import AdminSchedulingMap from "./AdminSchedulingMap";

function Dashboard() {
  const [ready, setReady] = useState(false);
  const [sessionRole, setSessionRole] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(async ({ data }) => {
        if (cancelled) return;
        const r = data.role === "superadmin" ? "superadmin" : data.role === "admin" ? "admin" : "customer";
        const stored = getRole();
        if (r !== stored) {
          try {
            const { data: tok } = await axios.post("/user/refresh-token", {}, { headers: getAuthHeaders() });
            if (tok.token) localStorage.setItem("token", tok.token);
            const tr = tok.role === "superadmin" ? "superadmin" : tok.role === "admin" ? "admin" : "customer";
            setRole(tr);
            setSessionRole(tr);
            return;
          } catch {
            /* fall through to set from settings */
          }
        }
        setRole(r);
        setSessionRole(r);
      })
      .catch(() => {
        if (!cancelled) {
          const r = getRole();
          setSessionRole(r === "superadmin" ? "superadmin" : r === "admin" ? "admin" : "customer");
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessionRole === "superadmin") {
    return <Navigate to="/platform" replace />;
  }

  return <DashboardWithNav isAdmin={sessionRole === "admin"} />;
}

const ADMIN_TAB_PATHS = ["website", "jobs", "catalog", "billing", "account"];
const CUSTOMER_TAB_PATHS = ["account", "services"];

function dashboardSectionFromPath(pathname) {
  const rest = pathname.replace(/^\/dashboard\/?/, "").split("/")[0] || "";
  return rest;
}

function DashboardThemeToggle() {
  const { mode, setColorMode } = useColorMode();
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.5, alignSelf: "center", flexShrink: 0 }}>
      <LightModeOutlinedIcon sx={{ fontSize: 18, opacity: mode === "light" ? 1 : 0.35 }} color="action" />
      <Switch
        size="small"
        checked={mode === "dark"}
        onChange={(_, c) => setColorMode(c ? "dark" : "light")}
        inputProps={{ "aria-label": "Use dark appearance for dashboard" }}
        color="default"
      />
      <DarkModeOutlinedIcon sx={{ fontSize: 18, opacity: mode === "dark" ? 1 : 0.35 }} color="action" />
    </Stack>
  );
}

function DashboardTopNav({ isAdmin }) {
  const location = useLocation();
  const g = useAppGlass();
  const raw = dashboardSectionFromPath(location.pathname);
  const validPaths = isAdmin ? ADMIN_TAB_PATHS : CUSTOMER_TAB_PATHS;
  const section = validPaths.includes(raw) ? raw : false;

  const tabs = isAdmin
    ? [
        { label: "Website", path: "website", hint: "Hero, quote link & embed" },
        { label: "Jobs", path: "jobs", hint: "Service jobs & quote inbox" },
        { label: "Catalog", path: "catalog", hint: "Quote page services" },
        { label: "Billing", path: "billing", hint: "Clibbins plan & Stripe" },
        { label: "Account", path: "account", hint: "Your profile" },
      ]
    : [
        { label: "Account", path: "account", hint: "Profile & contact" },
        { label: "My services", path: "services", hint: "Requests & status" },
      ];

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1190,
        ...g.panelStrong,
        borderRadius: 0,
        borderBottom: (theme) =>
          theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(255,255,255,0.55)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark" ? "0 4px 24px rgba(0,0,0,0.35)" : "0 4px 24px rgba(0,0,0,0.06)",
        px: { xs: 0.5, sm: 1 },
        pt: 0.5,
        pb: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
        <Tabs
          value={section}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              letterSpacing: "-0.01em",
            },
            "& .Mui-selected": {
              color: "primary.main",
            },
          }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.path}
              label={t.label}
              value={t.path}
              component={RouterLink}
              to={t.path}
              title={t.hint}
            />
          ))}
        </Tabs>
        </Box>
        <DashboardThemeToggle />
      </Box>
    </Box>
  );
}

function DashboardWithNav({ isAdmin }) {
  const defaultPath = isAdmin ? "website" : "account";

  return (
    <Box sx={{ minHeight: "100%", pt: 10 }}>
      <DashboardTopNav isAdmin={isAdmin} />
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: 2,
          pt: 3,
          pb: 4,
        }}
      >
        <Routes>
          <Route index element={<Navigate to={defaultPath} replace />} />
          {isAdmin ? (
            <>
              <Route
                path="website"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Website
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Set your <strong>customer quote link</strong> first, then hero copy and colors, embed snippets, and
                      landing template.
                    </Typography>
                    <OperatorQuoteLinkPanel />
                    <HomeHeroSettings />
                    <WebsiteBrandingPanel />
                  </>
                }
              />
              <Route path="jobs" element={<AdminJobsAndQuoting />} />
              <Route
                path="catalog"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Service catalog
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                      What appears on the public quote page (<strong>/quote</strong>) and in the quote flow.
                    </Typography>
                    <CreateJob />
                  </>
                }
              />
              <Route
                path="billing"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Billing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Pay for Clibbins with Stripe, and optionally connect your own Stripe account to charge customers.
                    </Typography>
                    <BillingPanel />
                  </>
                }
              />
              <Route
                path="account"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Your account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Business contact details for your team record.
                    </Typography>
                    <AccountCard variant="admin" />
                  </>
                }
              />
            </>
          ) : (
            <>
              <Route
                path="account"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      My lawn care
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Your profile and how we reach you.
                    </Typography>
                    <AccountCard variant="customer" />
                  </>
                }
              />
              <Route path="services" element={<CustomerServicesSection />} />
            </>
          )}
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function CustomerLinkOperator({ linkedSlug, onLinked }) {
  const g = useAppGlass();
  const [slugInput, setSlugInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const link = async () => {
    setBusy(true);
    setErr("");
    try {
      await axios.post(
        "/user/link-operator",
        { operatorQuoteSlug: slugInput.trim().toLowerCase() },
        { headers: getAuthHeaders() }
      );
      setSlugInput("");
      onLinked();
    } catch (e) {
      setErr(e.response?.data?.message || "Could not link.");
    } finally {
      setBusy(false);
    }
  };

  if (linkedSlug) {
    return (
      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
        Your requests go to the business at <strong>/quote/{linkedSlug}</strong> (their catalog loads below).
      </Alert>
    );
  }

  return (
    <Box sx={{ ...g.panel, borderRadius: 3, p: 2, mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Link your lawn care provider
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
        Enter the last part of their quote link (e.g. <strong>joes-lawn</strong> from <strong>/quote/joes-lawn</strong>
        ). Required when more than one business uses Clibbins.
      </Typography>
      {err ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr("")}>
          {err}
        </Alert>
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Quote link slug"
          placeholder="joes-lawn"
          value={slugInput}
          onChange={(e) => setSlugInput(e.target.value)}
          sx={{ ...g.textField, minWidth: 200 }}
        />
        <Button variant="contained" onClick={link} disabled={busy || !slugInput.trim()}>
          {busy ? "Saving…" : "Link business"}
        </Button>
      </Stack>
    </Box>
  );
}

function CustomerServicesSection() {
  const [jobsRefresh, setJobsRefresh] = useState(0);
  const [operatorSlug, setOperatorSlug] = useState("");

  const loadSlug = useCallback(() => {
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => setOperatorSlug(data.linkedOperatorQuoteSlug || ""))
      .catch(() => setOperatorSlug(""));
  }, []);

  useEffect(() => {
    loadSlug();
  }, [loadSlug, jobsRefresh]);

  return (
    <>
      <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
        My services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
        Request work and track status — use your provider&apos;s catalog once linked.
      </Typography>
      <CustomerLinkOperator linkedSlug={operatorSlug} onLinked={loadSlug} />
      <RequestServiceForm operatorQuoteSlug={operatorSlug} onCreated={() => setJobsRefresh((n) => n + 1)} />
      <CustomerJobsGrid refreshKey={jobsRefresh} />
    </>
  );
}

export default Dashboard;

function LogOutButton() {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      color="inherit"
      size="small"
      sx={{ mt: 1 }}
      onClick={() => {
        clearAuth();
        navigate("/", { replace: true });
      }}
    >
      Log out
    </Button>
  );
}

const JOBS_SUB_SERVICE = "service";
const JOBS_SUB_SCHEDULE = "schedule";
const JOBS_SUB_CREW = "crew";
const JOBS_SUB_NOTIFICATIONS = "notifications";
const JOBS_SUB_CUSTOMERS = "customers";
const JOBS_SUB_QUOTE_REQUESTS = "quote_requests";

function scheduleDateToday() {
  return new Date().toISOString().slice(0, 10);
}

const SCHEDULE_VIEW_LIST = "list";
const SCHEDULE_VIEW_CALENDAR = "calendar";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymFromIsoDate(iso) {
  if (typeof iso !== "string" || iso.length < 7) return scheduleDateToday().slice(0, 7);
  return iso.slice(0, 7);
}

function sortCrewKeysDash(keys) {
  const uniq = [...new Set(keys)];
  const un = uniq.filter((k) => k === "unassigned");
  const rest = uniq.filter((k) => k !== "unassigned").sort((a, b) => a.localeCompare(b));
  return [...un, ...rest];
}

function bucketJobsByCrewKey(jobList) {
  const m = new Map();
  for (const j of jobList || []) {
    const ck = j.assignedCrewMemberId ? String(j.assignedCrewMemberId) : "unassigned";
    if (!m.has(ck)) m.set(ck, []);
    m.get(ck).push(j);
  }
  return m;
}

function flattenJobBuckets(buckets) {
  const out = [];
  for (const ck of sortCrewKeysDash([...buckets.keys()])) {
    const arr = [...(buckets.get(ck) || [])];
    arr.forEach((j, i) => {
      out.push({ ...j, routeOrder: i });
    });
  }
  return out;
}

function addCalendarMonthYm(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yy}-${mm}`;
}

/** @returns {(string|null)[]} ISO date strings for month cells; null = padding before day 1 */
function monthGridCells(year, monthIndex0) {
  const firstWeekday = new Date(year, monthIndex0, 1).getDay();
  const daysInMonth = new Date(year, monthIndex0 + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    const iso = `${year}-${String(monthIndex0 + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    cells.push(iso);
  }
  return cells;
}

const OUTBOUND_CHANNEL_OPTIONS = [
  { value: "none", label: "Off" },
  { value: "email", label: "Email only" },
  { value: "sms", label: "SMS only" },
  { value: "both", label: "Email & SMS" },
];

function ServiceJobsLeadSummary({ onConfigureNotifications, refreshKey }) {
  const g = useAppGlass();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => setEmail(data.quoteNotificationEmail || ""))
      .catch(() => setEmail(""))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 2,
        p: 2,
        mb: 2,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Fast quote inbox
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {email.trim() ? (
              <>
                Leads are emailed to <strong>{email}</strong>.
              </>
            ) : (
              <>No notification email set yet — configure it under Notifications.</>
            )}
          </Typography>
        )}
      </Box>
      <Button variant="outlined" size="small" onClick={onConfigureNotifications}>
        Configure notifications
      </Button>
    </Box>
  );
}

function OutboundNotificationChannels() {
  const g = useAppGlass();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customerOutboundChannel, setCustomerOutboundChannel] = useState("email");
  const [crewOutboundChannel, setCrewOutboundChannel] = useState("email");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/site-settings/notifications", { headers: getAuthHeaders() })
      .then(({ data }) => {
        const n = data.notifications;
        if (cancelled || !n) return;
        setCustomerOutboundChannel(n.customerOutboundChannel || "email");
        setCrewOutboundChannel(n.crewOutboundChannel || "email");
      })
      .catch(() => {
        if (!cancelled) setMessage({ type: "error", text: "Could not load notification channels." });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const { data } = await axios.patch(
        "/site-settings/notifications",
        { customerOutboundChannel, crewOutboundChannel },
        { headers: getAuthHeaders() }
      );
      const n = data.notifications;
      if (n) {
        setCustomerOutboundChannel(n.customerOutboundChannel);
        setCrewOutboundChannel(n.crewOutboundChannel);
      }
      setMessage({ type: "success", text: "Saved outbound notification preferences." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [customerOutboundChannel, crewOutboundChannel]);

  const smsChosen =
    customerOutboundChannel === "sms" ||
    customerOutboundChannel === "both" ||
    crewOutboundChannel === "sms" ||
    crewOutboundChannel === "both";

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Customer & crew alerts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Choose how your organization reaches <strong>customers</strong> and <strong>crews</strong> for job-related
        messages. Email uses your server SMTP; SMS requires a provider (not configured yet).
      </Typography>
      {smsChosen ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          SMS delivery is not wired yet — preferences are saved for when Twilio or similar is added.
        </Alert>
      ) : null}
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 200, ...g.textField }} size="small">
              <InputLabel id="cust-ch-label">Customers</InputLabel>
              <Select
                labelId="cust-ch-label"
                label="Customers"
                value={customerOutboundChannel}
                onChange={(e) => setCustomerOutboundChannel(e.target.value)}
              >
                {OUTBOUND_CHANNEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200, ...g.textField }} size="small">
              <InputLabel id="crew-ch-label">Crews</InputLabel>
              <Select
                labelId="crew-ch-label"
                label="Crews"
                value={crewOutboundChannel}
                onChange={(e) => setCrewOutboundChannel(e.target.value)}
              >
                {OUTBOUND_CHANNEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save channels"}
          </Button>
        </>
      )}
    </Box>
  );
}

function JobsNotificationsPanel({ onInboxSaved }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Configure the <strong>Fast quote</strong> email inbox. Set how job-related messages should eventually reach
        customers and crews (SMS options save for a future provider; email works when SMTP is configured on the
        server).
      </Typography>
      <QuoteNotificationSettings onSaved={onInboxSaved} />
      <Divider sx={{ my: 2, opacity: 0.5 }} />
      <OutboundNotificationChannels />
    </>
  );
}

function AdminCrewPanel() {
  const g = useAppGlass();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/crew", { headers: getAuthHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError("Could not load crew.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditId(null);
    setFirstname("");
    setLastname("");
    setPhone("");
    setEmail("");
    setNotes("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setFirstname(row.firstname || "");
    setLastname(row.lastname || "");
    setPhone(row.phone || "");
    setEmail(row.email || "");
    setNotes(row.notes || "");
    setDialogOpen(true);
  };

  const saveCrew = async () => {
    if (!firstname.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.patch(
          `/crew/${editId}`,
          { firstname, lastname, phone, email, notes },
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(
          "/crew",
          { firstname, lastname, phone, email, notes },
          { headers: getAuthHeaders() }
        );
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ...g.panel, borderRadius: 3, p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Crew & field staff
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Contacts for assignments and route notifications (email uses addresses here when crew alerts are on).
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <Button startIcon={<PersonAddIcon />} variant="outlined" sx={{ mb: 2 }} onClick={openNew}>
        Add crew member
      </Button>
      {loading ? (
        <CircularProgress size={24} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No crew yet. Add someone who runs routes.</Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Box>
                  <Typography fontWeight={600}>
                    {r.firstname} {r.lastname}
                    {r.active === false ? " (inactive)" : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[r.phone, r.email].filter(Boolean).join(" · ") || "—"}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => openEdit(r)}>
                  Edit
                </Button>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editId ? "Edit crew member" : "Add crew member"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="First name"
            fullWidth
            required
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            sx={{ ...g.textField }}
          />
          <TextField
            margin="dense"
            label="Last name"
            fullWidth
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            sx={{ ...g.textField }}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ ...g.textField }}
          />
          <TextField
            margin="dense"
            label="Email (for assignments)"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ ...g.textField }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ ...g.textField }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveCrew} disabled={saving || !firstname.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AdminScheduleRoutesPanel() {
  const g = useAppGlass();
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [scheduleDate, setScheduleDate] = useState(scheduleDateToday);
  const [scheduleViewMode, setScheduleViewMode] = useState(SCHEDULE_VIEW_CALENDAR);
  const [calendarMonthYm, setCalendarMonthYm] = useState(() => ymFromIsoDate(scheduleDateToday()));
  const [monthJobs, setMonthJobs] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [directionsSummary, setDirectionsSummary] = useState("");
  const [routeDoc, setRouteDoc] = useState({ routeSegments: [], encodedPolyline: "", directionsSummary: "" });
  const [savingRoute, setSavingRoute] = useState(false);

  const loadMonthJobs = useCallback(async () => {
    setLoadingMonth(true);
    try {
      const [y, m] = calendarMonthYm.split("-").map(Number);
      const from = `${calendarMonthYm}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const to = `${calendarMonthYm}-${String(lastDay).padStart(2, "0")}`;
      const { data } = await axios.get("/jobs/schedule-range", {
        headers: getAuthHeaders(),
        params: { from, to },
      });
      setMonthJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch {
      setMonthJobs([]);
    } finally {
      setLoadingMonth(false);
    }
  }, [calendarMonthYm]);

  useEffect(() => {
    if (scheduleViewMode !== SCHEDULE_VIEW_CALENDAR) return;
    loadMonthJobs();
  }, [scheduleViewMode, loadMonthJobs]);

  const jobsByDate = useMemo(() => {
    const m = new Map();
    monthJobs.forEach((j) => {
      const k = (j.scheduledDate || "").trim();
      if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(j);
    });
    return m;
  }, [monthJobs]);

  const displayMonthLabel = useMemo(() => {
    const [y, mo] = calendarMonthYm.split("-").map(Number);
    return new Date(y, mo - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  }, [calendarMonthYm]);

  const calendarGrid = useMemo(() => {
    const [y, mo] = calendarMonthYm.split("-").map(Number);
    return monthGridCells(y, mo - 1);
  }, [calendarMonthYm]);

  const loadSchedule = useCallback(async (options = {}) => {
    const showLoading = options.showLoading !== false;
    if (showLoading) setLoading(true);
    setError("");
    try {
      const { data: sched } = await axios.get("/jobs/schedule", {
        headers: getAuthHeaders(),
        params: { date: scheduleDate },
      });
      const jobsList = Array.isArray(sched.jobs) ? sched.jobs : [];
      const superOpId =
        getRole() === "superadmin"
          ? jobsList.map((j) => j.operatorId).find((id) => id && String(id).trim())
          : null;
      const routeParams = superOpId ? { operatorId: superOpId } : {};
      const crewReq =
        getRole() === "superadmin" && !superOpId
          ? Promise.resolve({ data: [] })
          : axios.get("/crew", {
              headers: getAuthHeaders(),
              params: superOpId ? { operatorId: superOpId } : {},
            });
      const [{ data: crewList }, routeRes] = await Promise.all([
        crewReq,
        axios
          .get(`/jobs/routes/${scheduleDate}`, { headers: getAuthHeaders(), params: routeParams })
          .catch(() => ({ data: null })),
      ]);
      setJobs(jobsList);
      setCrew(Array.isArray(crewList) ? crewList : []);
      if (routeRes && routeRes.data) {
        setRouteDoc(routeRes.data);
        setDirectionsSummary(routeRes.data.directionsSummary || "");
      } else {
        setRouteDoc({ routeSegments: [], encodedPolyline: "", directionsSummary: "" });
        setDirectionsSummary("");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Could not load schedule.";
      if (showLoading) {
        setError(msg);
        setJobs([]);
        setRouteDoc({ routeSegments: [], encodedPolyline: "", directionsSummary: "" });
        setDirectionsSummary("");
      } else {
        setError(msg);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [scheduleDate]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const moveJobInCrew = (crewKey, indexInGroup, dir) => {
    setJobs((prev) => {
      const buckets = bucketJobsByCrewKey(prev);
      const list = [...(buckets.get(crewKey) || [])];
      if (list.length < 2) return prev;
      const ni = indexInGroup + dir;
      if (ni < 0 || ni >= list.length) return prev;
      const a = list[indexInGroup];
      const b = list[ni];
      list[indexInGroup] = b;
      list[ni] = a;
      buckets.set(crewKey, list);
      return flattenJobBuckets(buckets);
    });
  };

  const saveRouteOrder = async () => {
    setSavingRoute(true);
    setError("");
    try {
      const buckets = bucketJobsByCrewKey(jobs);
      const segments = sortCrewKeysDash([...buckets.keys()]).map((crewKey) => ({
        crewKey,
        stops: (buckets.get(crewKey) || []).map((row, i) => ({ jobId: row.id, order: i })),
      }));
      const body = { segments };
      if (getRole() === "superadmin") {
        const oid = jobs.map((j) => j.operatorId).find((id) => id && String(id).trim());
        if (oid) body.operatorId = oid;
      }
      await axios.put(`/jobs/routes/${scheduleDate}/stops`, body, { headers: getAuthHeaders() });
      await loadSchedule();
      if (ymFromIsoDate(scheduleDate) === calendarMonthYm) loadMonthJobs();
    } catch (err) {
      setError(err.response?.data?.message || "Could not save route.");
    } finally {
      setSavingRoute(false);
    }
  };

  const computeDirections = async () => {
    setSavingRoute(true);
    setError("");
    try {
      const postBody = {};
      const params = {};
      if (getRole() === "superadmin") {
        const oid = jobs.map((j) => j.operatorId).find((id) => id && String(id).trim());
        if (oid) {
          postBody.operatorId = oid;
          params.operatorId = oid;
        }
      }
      const { data } = await axios.post(
        `/jobs/routes/${scheduleDate}/directions`,
        postBody,
        { headers: getAuthHeaders(), params }
      );
      setDirectionsSummary(data.directionsSummary || "");
      setRouteDoc((prev) => ({
        ...prev,
        routeSegments: data.routeSegments || prev.routeSegments || [],
        encodedPolyline: data.encodedPolyline || prev.encodedPolyline,
        directionsSummary: data.directionsSummary || prev.directionsSummary,
      }));
      await loadSchedule({ showLoading: false });
      if (ymFromIsoDate(scheduleDate) === calendarMonthYm) loadMonthJobs();
    } catch (err) {
      setError(err.response?.data?.message || "Directions failed. Geocode addresses and set GOOGLE_MAPS_API_KEY.");
    } finally {
      setSavingRoute(false);
    }
  };

  const patchJob = async (id, payload) => {
    try {
      const { data } = await axios.patch(`/jobs/${id}`, payload, { headers: getAuthHeaders() });
      if (payload.assignedCrewMemberId !== undefined) {
        await loadSchedule();
      } else {
        setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...data } : j)));
      }
      if (ymFromIsoDate(scheduleDate) === calendarMonthYm) loadMonthJobs();
    } catch (err) {
      setError(err.response?.data?.message || "Update failed.");
    }
  };

  const geocodeJob = async (id) => {
    try {
      const { data } = await axios.post(`/jobs/${id}/geocode`, {}, { headers: getAuthHeaders() });
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...data } : j)));
      if (ymFromIsoDate(scheduleDate) === calendarMonthYm) loadMonthJobs();
    } catch (err) {
      setError(err.response?.data?.message || "Geocode failed.");
    }
  };

  const groupedEntries = useMemo(() => {
    const buckets = bucketJobsByCrewKey(jobs);
    return sortCrewKeysDash([...buckets.keys()]).map((crewKey) => {
      const list = buckets.get(crewKey) || [];
      let label = "Unassigned";
      if (crewKey !== "unassigned") {
        const cm = crew.find((c) => String(c.id) === String(crewKey));
        label = cm
          ? [cm.firstname, cm.lastname].filter(Boolean).join(" ").trim() || cm.email || `Crew (${String(crewKey).slice(-6)})`
          : `Crew (${String(crewKey).slice(-6)})`;
      }
      return { crewKey, label, list };
    });
  }, [jobs, crew]);

  const handleScheduleViewChange = useCallback((_, v) => {
    if (v == null) return;
    setScheduleViewMode(v);
    if (v === SCHEDULE_VIEW_CALENDAR) {
      setCalendarMonthYm(ymFromIsoDate(scheduleDate));
    }
  }, [scheduleDate]);

  const todayIso = scheduleDateToday();

  const directionsAlert =
    directionsSummary ? (
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2, whiteSpace: "pre-line" }}>
        {directionsSummary}
      </Alert>
    ) : null;

  const routeMap =
    mapsKey && jobs.length > 0 ? (
      <Box sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
        <AdminSchedulingMap
          apiKey={mapsKey}
          jobs={jobs}
          routeSegments={routeDoc.routeSegments || []}
        />
      </Box>
    ) : null;

  return (
    <Box sx={{ ...g.panel, borderRadius: 3, p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Daily schedule & routes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        <strong>Calendar</strong> — pick a month and day; the <strong>map below</strong> shows stops for that day and any
        saved driving routes (after you compute directions in List). <strong>List</strong> — reorder by crew, save route,
        compute directions, same map.
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scheduleViewMode}
          onChange={handleScheduleViewChange}
          aria-label="Schedule view"
        >
          <ToggleButton value={SCHEDULE_VIEW_CALENDAR}>
            <CalendarMonthIcon sx={{ fontSize: 18, mr: 0.75 }} />
            Calendar
          </ToggleButton>
          <ToggleButton value={SCHEDULE_VIEW_LIST}>
            <ViewListIcon sx={{ fontSize: 18, mr: 0.75 }} />
            List
          </ToggleButton>
        </ToggleButtonGroup>
        {scheduleViewMode === SCHEDULE_VIEW_LIST ? (
          <TextField
            type="date"
            label="Schedule date"
            size="small"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...g.textField, minWidth: 160 }}
          />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Selected: <strong>{scheduleDate}</strong>
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setScheduleDate(todayIso)}>
              Today
            </Button>
          </Stack>
        )}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {scheduleViewMode === SCHEDULE_VIEW_CALENDAR ? (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <IconButton aria-label="Previous month" onClick={() => setCalendarMonthYm((y) => addCalendarMonthYm(y, -1))}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={700}>
              {displayMonthLabel}
            </Typography>
            <IconButton aria-label="Next month" onClick={() => setCalendarMonthYm((y) => addCalendarMonthYm(y, 1))}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>
          {loadingMonth ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading month…
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 0.75,
                }}
              >
                {WEEKDAY_LABELS.map((wd) => (
                  <Typography key={wd} variant="caption" color="text.secondary" textAlign="center" fontWeight={600}>
                    {wd}
                  </Typography>
                ))}
                {calendarGrid.map((iso, idx) =>
                  iso ? (
                    <Paper
                      key={iso}
                      variant="outlined"
                      onClick={() => setScheduleDate(iso)}
                      sx={{
                        p: 1,
                        minHeight: 76,
                        cursor: "pointer",
                        borderRadius: .75,
                        borderWidth: iso === scheduleDate ? 2 : 1,
                        borderColor: iso === scheduleDate ? "primary.main" : "divider",
                        bgcolor: iso === todayIso ? "action.selected" : "background.paper",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {parseInt(iso.slice(8), 10)}
                      </Typography>
                      {jobsByDate.has(iso) ? (
                        <Chip
                          size="small"
                          label={`${jobsByDate.get(iso).length} job${jobsByDate.get(iso).length === 1 ? "" : "s"}`}
                          sx={{ mt: 0.75, maxWidth: "100%" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          —
                        </Typography>
                      )}
                    </Paper>
                  ) : (
                    <Box key={`pad-${idx}`} />
                  )
                )}
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }} fontWeight={600}>
                Map — {scheduleDate}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Geocoded job locations, crew-lane stop labels (A1, B2, …), and polylines from{" "}
                <strong>Compute driving directions</strong> (run in List view). No API key = map hidden.
              </Typography>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    Loading day…
                  </Typography>
                </Box>
              ) : jobs.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                  No jobs on this day. Choose another date or set schedules in <strong>Service jobs</strong>.
                </Typography>
              ) : (
                <>
                  {directionsAlert}
                  {routeMap}
                  {!mapsKey ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> to show the map.
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    To reorder stops, assign crew, or compute directions, switch to <strong>List</strong>.
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>
      ) : loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {jobs.length === 0 ? (
            <Typography color="text.secondary">No jobs on this date. Set scheduled date on a job in Service jobs.</Typography>
          ) : (
            <>
              <Stack spacing={2} sx={{ mb: 2 }}>
                {groupedEntries.map(({ crewKey, label, list }) => (
                  <Paper key={crewKey} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      {label}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {list.map((j, indexInGroup) => (
                        <Box
                          key={j.id}
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "center",
                            py: 0.5,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography sx={{ minWidth: 120 }} variant="body2">
                            #{indexInGroup + 1} {j.customerName || j.customerEmail}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={j.assignedCrewMemberId || ""}
                              displayEmpty
                              onChange={(e) =>
                                patchJob(j.id, {
                                  assignedCrewMemberId: e.target.value || null,
                                })
                              }
                            >
                              <MenuItem value="">Unassigned</MenuItem>
                              {crew.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.firstname} {c.lastname}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography variant="body2" sx={{ flex: "1 1 160px", minWidth: 100 }} noWrap title={j.propertyAddress}>
                            {j.propertyAddress || "—"}
                          </Typography>
                          <Tooltip title="Move earlier in this crew route">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveJobInCrew(crewKey, indexInGroup, -1)}
                                disabled={indexInGroup <= 0}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move later in this crew route">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveJobInCrew(crewKey, indexInGroup, 1)}
                                disabled={indexInGroup >= list.length - 1}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button size="small" variant="text" onClick={() => geocodeJob(j.id)}>
                            Geocode
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <Button variant="contained" onClick={saveRouteOrder} disabled={savingRoute}>
                  Save route order
                </Button>
                <Button variant="outlined" onClick={computeDirections} disabled={savingRoute}>
                  Compute driving directions
                </Button>
              </Stack>
              {directionsAlert}
              {routeMap}
              {jobs.length > 0 && !mapsKey ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> to show the map.
                </Typography>
              ) : null}
            </>
          )}
        </>
      )}
    </Box>
  );
}

function quoteStatusLabel(status) {
  const m = {
    new: "New",
    in_review: "In review",
    quoted: "Quoted",
    partially_converted: "Partial",
    converted: "Converted",
    lost: "Lost",
  };
  return m[status] || status;
}

function AdminQuoteRequestsPanel() {
  const g = useAppGlass();
  const [quotes, setQuotes] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listErr, setListErr] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [quoteStatus, setQuoteStatus] = useState("new");
  const [savingMeta, setSavingMeta] = useState(false);
  const [weeksAhead, setWeeksAhead] = useState(8);
  const [lineKeysSel, setLineKeysSel] = useState(() => new Set());
  const [converting, setConverting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualErr, setManualErr] = useState("");
  const [manual, setManual] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    propertyAddress: "",
    serviceName: "",
    servicePrice: "",
    internalNotes: "",
    scheduleMonthly: false,
  });

  const loadList = useCallback(async () => {
    setListErr("");
    setListLoading(true);
    try {
      const { data } = await axios.get("/quotes", { headers: getAuthHeaders(), params: { limit: 100 } });
      setQuotes(Array.isArray(data.quotes) ? data.quotes : []);
    } catch (e) {
      setQuotes([]);
      setListErr(e.response?.data?.message || "Could not load quotes.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);
    setInternalNotes("");
    setQuoteStatus("new");
    setWeeksAhead(8);
    setLineKeysSel(new Set());
    try {
      const { data } = await axios.get(`/quotes/${id}`, { headers: getAuthHeaders() });
      setDetail(data);
      setInternalNotes(data.internalNotes || "");
      setQuoteStatus(data.status || "new");
      const conv = (data.lineItems || [])
        .filter((l) => l.status !== "converted" && l.status !== "cancelled")
        .map((l) => l.lineItemKey);
      setLineKeysSel(new Set(conv));
    } catch (e) {
      setDetailErr(e.response?.data?.message || "Could not load quote.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
  };

  const saveMeta = async () => {
    if (!detail) return;
    setSavingMeta(true);
    setDetailErr("");
    try {
      const { data } = await axios.patch(
        `/quotes/${detail.id}`,
        { internalNotes, status: quoteStatus },
        { headers: getAuthHeaders() }
      );
      setDetail(data);
      setInternalNotes(data.internalNotes || "");
      setQuoteStatus(data.status || "new");
      await loadList();
    } catch (e) {
      setDetailErr(e.response?.data?.message || "Save failed.");
    } finally {
      setSavingMeta(false);
    }
  };

  const toggleLine = (key) => {
    setLineKeysSel((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  const runConvert = async () => {
    if (!detail) return;
    const keys = [...lineKeysSel];
    if (keys.length === 0) {
      setDetailErr("Select at least one line to convert.");
      return;
    }
    setConverting(true);
    setDetailErr("");
    try {
      const { data } = await axios.post(
        `/quotes/${detail.id}/convert`,
        { weeksAhead, lineItemKeys: keys },
        { headers: getAuthHeaders() }
      );
      if (data.quote) {
        setDetail(data.quote);
        setInternalNotes(data.quote.internalNotes || "");
        setQuoteStatus(data.quote.status || "new");
        const conv = (data.quote.lineItems || [])
          .filter((l) => l.status !== "converted" && l.status !== "cancelled")
          .map((l) => l.lineItemKey);
        setLineKeysSel(new Set(conv));
      }
      await loadList();
    } catch (e) {
      setDetailErr(e.response?.data?.message || "Conversion failed.");
    } finally {
      setConverting(false);
    }
  };

  const submitManual = async () => {
    setManualErr("");
    const email = manual.email.trim().toLowerCase();
    if (!manual.firstName.trim() || !email) {
      setManualErr("First name and email are required.");
      return;
    }
    setManualSaving(true);
    try {
      await axios.post(
        "/quotes",
        {
          contact: {
            firstName: manual.firstName.trim(),
            lastName: manual.lastName.trim(),
            email,
            phone: manual.phone.trim(),
          },
          parcels: manual.propertyAddress.trim() ? [{ address: manual.propertyAddress.trim() }] : [],
          services: manual.serviceName.trim()
            ? [{ name: manual.serviceName.trim(), price: manual.servicePrice.trim() }]
            : [],
          scheduleMonthly: manual.scheduleMonthly,
          internalNotes: manual.internalNotes.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setManualOpen(false);
      setManual({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        propertyAddress: "",
        serviceName: "",
        servicePrice: "",
        internalNotes: "",
        scheduleMonthly: false,
      });
      await loadList();
    } catch (e) {
      setManualErr(e.response?.data?.message || "Could not create quote.");
    } finally {
      setManualSaving(false);
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Fast quote submissions from your branded link and from generic /quote (when this notification email was
          included) appear here, along with manual quotes. Convert to create lawn jobs on a rolling schedule.
        </Typography>
        <Button variant="contained" onClick={() => setManualOpen(true)} startIcon={<PostAddIcon />}>
          New manual quote
        </Button>
      </Stack>

      {listErr ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListErr("")}>
          {listErr}
        </Alert>
      ) : null}

      <Paper sx={{ ...g.panelStrong, borderRadius: 2, overflow: "hidden" }}>
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : quotes.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography color="text.secondary" align="center">
              No quote requests yet. Share your quote page link so submissions are saved here.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Lines</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} hover sx={{ cursor: "pointer" }} onClick={() => openDetail(q.id)}>
                  <TableCell>{q.createdAt ? new Date(q.createdAt).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{q.contact?.email || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[q.contact?.firstName, q.contact?.lastName].filter(Boolean).join(" ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={quoteStatusLabel(q.status)} variant="outlined" />
                  </TableCell>
                  <TableCell>{q.source === "web" ? "Web" : "Manual"}</TableCell>
                  <TableCell align="right">
                    {q.lineItemCount ?? 0}
                    {q.convertedCount ? (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({q.convertedCount} converted)
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Quote request</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailErr && !detail ? (
            <Alert severity="error">{detailErr}</Alert>
          ) : detail ? (
            <Stack spacing={2}>
              {detailErr ? <Alert severity="warning">{detailErr}</Alert> : null}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Contact
                </Typography>
                <Typography variant="body1">
                  {[detail.contact?.firstName, detail.contact?.lastName].filter(Boolean).join(" ") || "—"}
                </Typography>
                <Typography variant="body2">{detail.contact?.email}</Typography>
                {detail.contact?.phone ? <Typography variant="body2">{detail.contact.phone}</Typography> : null}
              </Box>
              {detail.parcels?.length ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Parcels
                  </Typography>
                  {(detail.parcels || []).map((p, i) => (
                    <Typography key={i} variant="body2">
                      {p.address || `Parcel ${i + 1}`}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              <Divider />
              <Typography variant="subtitle2">Line items</Typography>
              <Stack spacing={1}>
                {(detail.lineItems || []).map((line) => {
                  const done = line.status === "converted" || line.status === "cancelled";
                  return (
                    <Paper key={line.lineItemKey} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        {!done ? (
                          <Checkbox
                            checked={lineKeysSel.has(line.lineItemKey)}
                            onChange={() => toggleLine(line.lineItemKey)}
                            sx={{ p: 0, mt: -0.5 }}
                          />
                        ) : (
                          <Box sx={{ width: 42 }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {line.serviceName}
                            {line.servicePrice ? ` · ${line.servicePrice}` : ""}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {line.cadence}
                            {line.propertyAddress ? ` · ${line.propertyAddress}` : ""}
                          </Typography>
                          <Chip size="small" label={line.status} sx={{ mt: 0.5 }} />
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
              <Divider />
              <TextField
                label="Weeks ahead to materialize"
                type="number"
                inputProps={{ min: 1, max: 52 }}
                value={weeksAhead}
                onChange={(e) =>
                  setWeeksAhead(Math.min(52, Math.max(1, Number(e.target.value) || 8)))
                }
                helperText="Recurring lines get job rows for occurrences in this window; the series rolls forward later."
                fullWidth
                size="small"
              />
              <Button variant="contained" color="primary" onClick={runConvert} disabled={converting}>
                {converting ? "Converting…" : "Convert selected to jobs"}
              </Button>
              <Divider />
              <TextField
                label="Internal notes"
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <FormControl fullWidth size="small">
                <InputLabel id="quote-status-label">Quote status</InputLabel>
                <Select
                  labelId="quote-status-label"
                  label="Quote status"
                  value={quoteStatus}
                  onChange={(e) => setQuoteStatus(e.target.value)}
                >
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="in_review">In review</MenuItem>
                  <MenuItem value="quoted">Quoted</MenuItem>
                  <MenuItem value="lost">Lost</MenuItem>
                  {["partially_converted", "converted"].includes(quoteStatus) ? (
                    <MenuItem value={quoteStatus} disabled>
                      {quoteStatusLabel(quoteStatus)} (from line items)
                    </MenuItem>
                  ) : null}
                </Select>
              </FormControl>
              {["partially_converted", "converted"].includes(quoteStatus) ? (
                <Typography variant="caption" color="text.secondary">
                  Choose another status above to override, or leave as-is when saving notes only.
                </Typography>
              ) : null}
              <Button variant="outlined" onClick={saveMeta} disabled={savingMeta}>
                {savingMeta ? "Saving…" : "Save notes & status"}
              </Button>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualOpen} onClose={() => !manualSaving && setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New manual quote</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {manualErr ? <Alert severity="error">{manualErr}</Alert> : null}
            <TextField
              label="First name"
              value={manual.firstName}
              onChange={(e) => setManual((m) => ({ ...m, firstName: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Last name"
              value={manual.lastName}
              onChange={(e) => setManual((m) => ({ ...m, lastName: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              value={manual.email}
              onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Phone"
              value={manual.phone}
              onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Property address"
              value={manual.propertyAddress}
              onChange={(e) => setManual((m) => ({ ...m, propertyAddress: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Service name"
              value={manual.serviceName}
              onChange={(e) => setManual((m) => ({ ...m, serviceName: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Service price"
              value={manual.servicePrice}
              onChange={(e) => setManual((m) => ({ ...m, servicePrice: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={manual.scheduleMonthly}
                  onChange={(e) => setManual((m) => ({ ...m, scheduleMonthly: e.target.checked }))}
                />
              }
              label="Monthly cadence (vs one-time)"
            />
            <TextField
              label="Internal notes"
              value={manual.internalNotes}
              onChange={(e) => setManual((m) => ({ ...m, internalNotes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitManual} disabled={manualSaving}>
            {manualSaving ? "Saving…" : "Create quote"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AdminJobsAndQuoting() {
  const g = useAppGlass();
  const [sub, setSub] = useState(JOBS_SUB_SERVICE);
  const [leadStripVersion, setLeadStripVersion] = useState(0);

  return (
    <>
      <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
        Jobs & quoting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Service jobs, <strong>schedule &amp; routes</strong>, <strong>crew</strong>, <strong>quote requests</strong>, and{" "}
        <strong>Fast quote</strong> inbox. Crew alerts respect notification channels under Quote inbox.
      </Typography>
      <Box
        sx={{
          ...g.panelStrong,
          borderRadius: 2,
          px: 0.5,
          pt: 0.5,
          mb: 2,
          border: (theme) =>
            theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.55)",
        }}
      >
        <Tabs
          value={sub}
          onChange={(_, v) => setSub(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
            },
          }}
        >
          <Tab label="Service jobs" value={JOBS_SUB_SERVICE} />
          <Tab label="Quote requests" value={JOBS_SUB_QUOTE_REQUESTS} />
          <Tab label="Schedule & routes" value={JOBS_SUB_SCHEDULE} />
          <Tab label="Crew" value={JOBS_SUB_CREW} />
          <Tab label="Quote inbox & alerts" value={JOBS_SUB_NOTIFICATIONS} />
          <Tab label="Customers" value={JOBS_SUB_CUSTOMERS} />
        </Tabs>
      </Box>
      {sub === JOBS_SUB_SERVICE ? (
        <>
          <ServiceJobsLeadSummary
            onConfigureNotifications={() => setSub(JOBS_SUB_NOTIFICATIONS)}
            refreshKey={leadStripVersion}
          />
          <AdminJobsGrid />
        </>
      ) : sub === JOBS_SUB_QUOTE_REQUESTS ? (
        <AdminQuoteRequestsPanel />
      ) : sub === JOBS_SUB_SCHEDULE ? (
        <AdminScheduleRoutesPanel />
      ) : sub === JOBS_SUB_CREW ? (
        <AdminCrewPanel />
      ) : sub === JOBS_SUB_CUSTOMERS ? (
        <AdminCustomersPanel />
      ) : (
        <JobsNotificationsPanel onInboxSaved={() => setLeadStripVersion((v) => v + 1)} />
      )}
    </>
  );
}

function QuoteNotificationSettings({ onSaved }) {
  const g = useAppGlass();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
        if (!cancelled) {
          setEmail(data.quoteNotificationEmail || "");
        }
      } catch {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load settings. Try signing in again." });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/user/settings",
        { quoteNotificationEmail: email },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: "success",
        text: "Saved. Fast quote submissions from the quote page will be emailed to this address.",
      });
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [email, onSaved]);

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Quote notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Set the inbox that receives <strong>Fast quote</strong> requests from the public quote page (customer
        contact, properties, and selected services). Only administrator accounts can change this.
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <TextField
            label="Notification email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="office@yourbusiness.com"
            sx={{ mb: 2, ...g.textField }}
            helperText="SMTP must be configured on the server for emails to send."
          />
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save notification address"}
          </Button>
        </>
      )}
    </Box>
  );
}

function HomeHeroSettings() {
  const g = useAppGlass();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/site-settings/home-hero")
      .then(({ data }) => {
        const h = data.homeHero;
        if (cancelled || !h) return;
        setHeadline(typeof h.headline === "string" ? h.headline : "");
        setSubheadline(typeof h.subheadline === "string" ? h.subheadline : "");
        setImageUrl(typeof h.imageUrl === "string" ? h.imageUrl : "");
      })
      .catch(() => {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load home hero settings." });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/site-settings/home-hero",
        {
          headline: headline.trim(),
          subheadline: subheadline.trim(),
          imageUrl: imageUrl.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: "success",
        text: "Saved. Refresh the quote page to see the hero headline, supporting copy, and background image.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [headline, subheadline, imageUrl]);

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Quote page hero (first section)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        This is the top banner visitors see <strong>before</strong> they pick an address — headline, supporting
        text, and full-width background image (same area as the quote flow hero).
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <TextField
            label="Headline"
            required
            fullWidth
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            sx={{ mb: 2, ...g.textField }}
            helperText="Main call to action title (glass card on the hero)."
          />
          <TextField
            label="Supporting copy"
            fullWidth
            multiline
            minRows={3}
            value={subheadline}
            onChange={(e) => setSubheadline(e.target.value)}
            sx={{ mb: 2, ...g.textField }}
            helperText="Secondary line under the headline — explain the offer or next step."
          />
          <TextField
            label="Background image URL"
            fullWidth
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ mb: 2, ...g.textField }}
            helperText="Full URL (https://…) or site path (e.g. /logo192.png). Leave empty to keep the default bundled hero image."
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <Button variant="contained" color="primary" onClick={save} disabled={saving || !headline.trim()}>
              {saving ? "Saving…" : "Save hero"}
            </Button>
            <Button component={RouterLink} to="/quote" target="_blank" rel="noopener noreferrer" variant="outlined">
              Preview quote page
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

function jobStatusLabel(status, opts = {}) {
  const cv = opts.customerView;
  const s = {
    pending: cv ? "With your pro" : "Pending review",
    awaiting_customer: cv ? "Awaiting your approval" : "Awaiting customer",
    scheduled: "Scheduled",
    in_progress: "In progress",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return s[status] || String(status || "").replace(/_/g, " ") || "—";
}

/** Operator may pick these next statuses from the job grid (primary lifecycle is approve / send schedules). */
function operatorStatusChoices(current) {
  switch (current) {
    case "pending":
      return ["cancelled"];
    case "awaiting_customer":
      return ["cancelled"];
    case "scheduled":
      return ["in_progress", "cancelled"];
    case "in_progress":
      return ["completed", "cancelled"];
    default:
      return [];
  }
}

function AccountCard({ variant }) {
  const g = useAppGlass();
  const isAdmin = variant === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const load = useCallback(async () => {
    setMessage({ type: "", text: "" });
    try {
      const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
      setFirstname(data.firstname || "");
      setLastname(data.lastname || "");
      setPhone(data.phone || "");
      setAddress(data.address || "");
      setEmail(data.email || "");
    } catch {
      setMessage({ type: "error", text: "Could not load account. Try signing in again." });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
        if (!cancelled) {
          setFirstname(data.firstname || "");
          setLastname(data.lastname || "");
          setPhone(data.phone || "");
          setAddress(data.address || "");
          setEmail(data.email || "");
        }
      } catch {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load account. Try signing in again." });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/user/profile",
        { firstname, lastname, phone, address },
        { headers: getAuthHeaders() }
      );
      await load();
      setMessage({
        type: "success",
        text: isAdmin
          ? "Contact details saved. These stay on file for your team."
          : "Profile saved. We use this phone and service address when scheduling visits.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [firstname, lastname, phone, address, load, isAdmin]);

  if (loading) {
    return (
      <Box sx={{ ...g.panelStrong, borderRadius: 3, p: 2.5, mb: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...g.panelStrong,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {isAdmin ? "Administrator account" : "Your profile & service location"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, lineHeight: 1.5 }}>
        {isAdmin
          ? "Your sign-in email is fixed. Update your name, phone, and business or dispatch address below."
          : "Keep your contact info and the property we service up to date. Your email is your login and cannot be changed here."}
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <TextField
        label="Email (login)"
        fullWidth
        value={email}
        disabled
        sx={{ mb: 2, ...g.textField }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          label="First name"
          required
          value={firstname}
          onChange={(e) => setFirstname(e.target.value)}
          sx={{ flex: "1 1 160px", minWidth: 140, ...g.textField }}
        />
        <TextField
          label="Last name"
          value={lastname}
          onChange={(e) => setLastname(e.target.value)}
          sx={{ flex: "1 1 160px", minWidth: 140, ...g.textField }}
        />
      </Box>
      <TextField
        label="Phone"
        type="tel"
        fullWidth
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="(555) 123-4567"
        sx={{ mb: 2, ...g.textField }}
        helperText={isAdmin ? "Reachable number for crews or customers." : "Best number to reach you about visits."}
      />
      <TextField
        label={isAdmin ? "Business / dispatch address" : "Service property address"}
        fullWidth
        multiline
        minRows={2}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Street, city, state, ZIP"
        sx={{ mb: 2, ...g.textField }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <Button variant="contained" color="primary" onClick={save} disabled={saving || !firstname.trim()}>
          {saving ? "Saving…" : "Save profile"}
        </Button>
        <LogOutButton />
      </Box>
      <AccountRoleChangeSection />
    </Box>
  );
}

function AccountRoleChangeSection() {
  const g = useAppGlass();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/user/role-change-request", { headers: getAuthHeaders() });
      setInfo(data);
    } catch {
      setInfo({ eligible: false, request: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !info?.eligible) {
    return null;
  }

  const current = info.currentRole;
  const req = info.request;
  const pending = req?.status === "pending";
  const other = current === "customer" ? "admin" : "customer";
  const currentLabel =
    current === "admin" ? "lawn care operator" : "homeowner / customer";
  const otherLabel =
    other === "admin" ? "lawn care operator (business dashboard)" : "homeowner / customer";

  const upToDate =
    req?.status === "approved" && req.requestedRole === current;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMsg({ type: "", text: "" });
    try {
      await axios.post(
        "/user/role-change-request",
        { requestedRole: other, message: note.trim() },
        { headers: getAuthHeaders() }
      );
      setNote("");
      await load();
      setMsg({
        type: "success",
        text: "Request sent. The Clibbins platform team will review it. Your session will update automatically after approval.",
      });
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Could not submit request.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        pt: 3,
        borderTop: (theme) =>
          theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Wrong account type?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, lineHeight: 1.55 }}>
        You're signed in as a <strong>{currentLabel}</strong>. If you meant to use the other experience, send a request
        to the platform team—they can switch you between homeowner and operator.
      </Typography>
      {msg.text ? (
        <Alert severity={msg.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {msg.text}
        </Alert>
      ) : null}
      {upToDate ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Your account type is set to <strong>{currentLabel}</strong>. No change request is needed.
        </Alert>
      ) : null}
      {pending ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Pending request to switch to <strong>{otherLabel}</strong>
          {req.message ? ` — “${req.message.slice(0, 120)}${req.message.length > 120 ? "…" : ""}”` : ""}. We'll notify you
          when it's processed (your dashboard may refresh automatically).
        </Alert>
      ) : null}
      {!pending && !upToDate ? (
        <Box component="form" onSubmit={handleSubmit}>
          {req?.status === "rejected" ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Your last request was declined. You can submit a new one with more detail if needed.
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Request switch to: <strong>{otherLabel}</strong>
          </Typography>
          <TextField
            label="Optional note for the platform team"
            multiline
            minRows={2}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mb: 2, ...g.textField }}
            placeholder="e.g. I signed up as the wrong type, or my business manages multiple properties."
          />
          <Button type="submit" variant="outlined" disabled={submitting}>
            {submitting ? "Sending…" : "Submit request to change account type"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

function RequestServiceForm({ onCreated, operatorQuoteSlug = "" }) {
  const g = useAppGlass();
  const slug = (operatorQuoteSlug || "").trim().toLowerCase();
  const { services: catalogServices, loading: catalogLoading } = useCatalogServices(slug);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTimeWindow, setScheduledTimeWindow] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!catalogServices.length) return;
    if (!serviceId || !catalogServices.some((s) => s.id === serviceId)) {
      setServiceId(catalogServices[0].id);
    }
  }, [catalogServices, serviceId]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setPropertyAddress(data.address || "");
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = catalogServices.find((s) => s.id === serviceId) || catalogServices[0];

  const submit = useCallback(async () => {
    if (!selected) return;
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      const body = {
        serviceName: selected.name,
        servicePrice:
          quoteServiceDisplayPrice(
            {
              price: selected.price,
              pricingModel: selected.pricingModel,
              pricePerSqFt: selected.pricePerSqFt,
            },
            null
          ) || selected.price,
        propertyAddress: propertyAddress.trim(),
        notes: notes.trim(),
        scheduledDate: scheduledDate.trim(),
        scheduledTimeWindow: scheduledTimeWindow.trim(),
      };
      if (slug) {
        body.operatorQuoteSlug = slug;
      }
      await axios.post("/jobs", body, { headers: getAuthHeaders() });
      setMessage({ type: "success", text: "Request submitted. It will appear in your services list below." });
      setNotes("");
      setScheduledDate("");
      setScheduledTimeWindow("");
      if (onCreated) onCreated();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not submit request.",
      });
    } finally {
      setSubmitting(false);
    }
  }, [selected, propertyAddress, notes, scheduledDate, scheduledTimeWindow, onCreated, slug]);

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Request a service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Services match the <strong>quote page</strong> catalog. Your provider sees the request in the admin job list.
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingDefaults || catalogLoading || !catalogServices.length}>
        <InputLabel id="service-select-label">Service</InputLabel>
        <Select
          labelId="service-select-label"
          label="Service"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
        >
          {catalogServices.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} — {s.price}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Property address for this job"
        fullWidth
        multiline
        minRows={2}
        value={propertyAddress}
        onChange={(e) => setPropertyAddress(e.target.value)}
        sx={{ mb: 2, ...g.textField }}
        helperText="Defaults from your saved profile; you can change it per request."
      />
      <TextField
        label="Preferred date (optional)"
        type="date"
        fullWidth
        InputLabelProps={{ shrink: true }}
        value={scheduledDate}
        onChange={(e) => setScheduledDate(e.target.value)}
        sx={{ mb: 2, ...g.textField }}
      />
      <TextField
        label="Time window (optional, e.g. morning)"
        fullWidth
        value={scheduledTimeWindow}
        onChange={(e) => setScheduledTimeWindow(e.target.value)}
        sx={{ mb: 2, ...g.textField }}
      />
      <TextField
        label="Notes (optional)"
        fullWidth
        multiline
        minRows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        sx={{ mb: 2, ...g.textField }}
      />
      <Button
        variant="contained"
        onClick={submit}
        disabled={submitting || loadingDefaults || catalogLoading || !selected}
      >
        {submitting ? "Submitting…" : "Submit request"}
      </Button>
    </Box>
  );
}

function formatShortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

const customerJobColumnsBase = [
  { field: "serviceName", headerName: "Service", flex: 1, minWidth: 120 },
  { field: "servicePrice", headerName: "Price", width: 100 },
  { field: "propertyAddress", headerName: "Address", flex: 1, minWidth: 140 },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    valueFormatter: (p) => jobStatusLabel(p.value),
  },
  {
    field: "scheduledDate",
    headerName: "Preferred",
    width: 110,
    valueFormatter: (params) => params.value || "—",
  },
  {
    field: "scheduledTimeWindow",
    headerName: "Time",
    width: 100,
    valueFormatter: (params) => params.value || "—",
  },
  {
    field: "createdAt",
    headerName: "Requested",
    width: 110,
    valueFormatter: (params) => formatShortDate(params.value),
  },
];

function CustomerJobsGrid({ refreshKey }) {
  const g = useAppGlass();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState({ type: "", text: "" });
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineJobId, setDeclineJobId] = useState(null);
  const [declineNote, setDeclineNote] = useState("");
  const [busyId, setBusyId] = useState(null);

  const refreshRows = useCallback(() => {
    axios
      .get("/jobs", { headers: getAuthHeaders() })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get("/jobs", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load your services. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const approveJob = useCallback(async (jobId) => {
    setActionMsg({ type: "", text: "" });
    setBusyId(jobId);
    try {
      await axios.patch(`/jobs/${jobId}`, { customerDecision: "approve" }, { headers: getAuthHeaders() });
      setActionMsg({ type: "success", text: "You confirmed the visit — it's now scheduled." });
      refreshRows();
    } catch (err) {
      setActionMsg({ type: "error", text: err.response?.data?.message || "Could not confirm." });
    } finally {
      setBusyId(null);
    }
  }, [refreshRows]);

  const submitDecline = useCallback(async () => {
    if (!declineJobId) return;
    setActionMsg({ type: "", text: "" });
    setBusyId(declineJobId);
    try {
      await axios.patch(
        `/jobs/${declineJobId}`,
        { customerDecision: "decline", declineNote },
        { headers: getAuthHeaders() }
      );
      setActionMsg({ type: "success", text: "Sent back to your pro for changes." });
      setDeclineOpen(false);
      setDeclineJobId(null);
      setDeclineNote("");
      refreshRows();
    } catch (err) {
      setActionMsg({ type: "error", text: err.response?.data?.message || "Could not update." });
    } finally {
      setBusyId(null);
    }
  }, [declineJobId, declineNote, refreshRows]);

  const customerColumns = useMemo(
    () => [
      ...customerJobColumnsBase.filter((c) => c.field !== "createdAt"),
      {
        field: "proposal",
        headerName: "Your action",
        width: 228,
        sortable: false,
        renderCell: (params) => {
          if (params.row.status !== "awaiting_customer") {
            return (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            );
          }
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                disabled={busyId === params.row.id}
                onClick={() => approveJob(params.row.id)}
              >
                Approve
              </Button>
              <Button
                size="small"
                disabled={busyId === params.row.id}
                onClick={() => {
                  setDeclineJobId(params.row.id);
                  setDeclineNote("");
                  setDeclineOpen(true);
                }}
              >
                Send back
              </Button>
            </Stack>
          );
        },
      },
      ...customerJobColumnsBase.filter((c) => c.field === "createdAt"),
    ],
    [approveJob, busyId]
  );

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, px: 0.5 }}>
        Your services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5, lineHeight: 1.5 }}>
        New requests start as <strong>pending review</strong>. Your pro sets the date and details, then you’ll{" "}
        <strong>approve</strong> the visit or <strong>send it back</strong> for changes. Once you both agree, the job is{" "}
        <strong>scheduled</strong>, then <strong>in progress</strong> and <strong>completed</strong>.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      {actionMsg.text ? (
        <Alert
          severity={actionMsg.type === "success" ? "success" : "error"}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setActionMsg({ type: "", text: "" })}
        >
          {actionMsg.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: Math.max(320, Math.min(480, 80 + rows.length * 52)),
            width: "100%",
            minHeight: 320,
            borderRadius: 2,
            overflow: "hidden",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.6)",
            backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={customerColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            sx={{
              border: "none",
              color: "text.primary",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
              },
              "& .MuiDataGrid-cell": {
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              },
            }}
          />
        </Box>
      )}

      <Dialog open={declineOpen} onClose={() => !busyId && setDeclineOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send back to your pro?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional note (e.g. preferred days). They can revise the plan and send it again.
          </Typography>
          <TextField
            label="Note (optional)"
            fullWidth
            multiline
            minRows={2}
            value={declineNote}
            onChange={(e) => setDeclineNote(e.target.value)}
            sx={{ ...g.textField }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitDecline} disabled={!!busyId}>
            {busyId ? "Sending…" : "Send back"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const CUSTOMER_CONNECTION_FILTERS = [
  { value: "all", label: "All in workspace" },
  { value: "linked", label: "Linked dashboard account" },
  { value: "jobs_only", label: "Jobs only (not linked)" },
];

const CUSTOMER_JOBS_FILTERS = [
  { value: "any", label: "Any job activity" },
  { value: "open", label: "Has open work" },
  { value: "any_job", label: "Has any job" },
  { value: "none", label: "No jobs yet" },
];

function AdminCustomersPanel() {
  const g = useAppGlass();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [connection, setConnection] = useState("all");
  const [jobsFilter, setJobsFilter] = useState("any");
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, text: "", severity: "info" });
  const [confirmBulk, setConfirmBulk] = useState({ open: false, action: null });

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/jobs/customers", {
        headers: getAuthHeaders(),
        params: {
          page: page + 1,
          limit: pageSize,
          connection,
          jobs: jobsFilter,
          search: searchApplied || undefined,
        },
      });
      setRows(Array.isArray(data.customers) ? data.customers : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load customers.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, connection, jobsFilter, searchApplied]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [connection, jobsFilter, searchApplied]);

  const customerColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (p) => [p.row.firstname, p.row.lastname].filter(Boolean).join(" ") || "—",
      },
      { field: "email", headerName: "Email", flex: 1, minWidth: 180, sortable: false },
      { field: "phone", headerName: "Phone", width: 128, sortable: false },
      {
        field: "linked",
        headerName: "Linked",
        width: 96,
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.row.linked ? "Yes" : "No"}
            color={p.row.linked ? "success" : "default"}
            variant={p.row.linked ? "filled" : "outlined"}
          />
        ),
      },
      { field: "totalJobs", headerName: "Jobs", width: 72, sortable: false },
      { field: "openJobs", headerName: "Open", width: 72, sortable: false },
      {
        field: "lastJobAt",
        headerName: "Last job",
        width: 118,
        sortable: false,
        valueFormatter: (p) => (p.value ? formatShortDate(p.value) : "—"),
      },
    ],
    []
  );

  const handleSelectAllMatching = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get("/jobs/customers/ids", {
        headers: getAuthHeaders(),
        params: {
          connection,
          jobs: jobsFilter,
          search: searchApplied || undefined,
        },
      });
      const ids = Array.isArray(data.ids) ? data.ids : [];
      setSelectionModel(ids);
      setSnack({
        open: true,
        severity: "success",
        text: ids.length ? `${ids.length} customer${ids.length === 1 ? "" : "s"} selected (max 500).` : "No matches.",
      });
    } catch (err) {
      setError(err.response?.data?.message || "Could not load matching customers.");
    }
  }, [connection, jobsFilter, searchApplied]);

  const runBulk = useCallback(
    async (action) => {
      if (!selectionModel.length) {
        setSnack({ open: true, severity: "warning", text: "Select at least one customer." });
        return;
      }
      setActionLoading(true);
      setError("");
      try {
        const { data } = await axios.post(
          "/jobs/customers/bulk",
          {
            action,
            selection: { mode: "ids", customerIds: selectionModel },
          },
          { headers: getAuthHeaders() }
        );
        const parts = [];
        if (data.unlinked) parts.push(`${data.unlinked} unlinked`);
        if (data.jobsCancelled) parts.push(`${data.jobsCancelled} open job(s) cancelled`);
        setSnack({
          open: true,
          severity: "success",
          text: parts.length ? parts.join(" · ") : data.message || "Done.",
        });
        setSelectionModel([]);
        await loadCustomers();
      } catch (err) {
        setError(err.response?.data?.message || "Action failed.");
      } finally {
        setActionLoading(false);
        setConfirmBulk({ open: false, action: null });
      }
    },
    [selectionModel, loadCustomers]
  );

  const runBulkFiltered = useCallback(
    async (action) => {
      setActionLoading(true);
      setError("");
      try {
        const { data } = await axios.post(
          "/jobs/customers/bulk",
          {
            action,
            selection: {
              mode: "filtered",
              filter: { connection, jobs: jobsFilter, search: searchApplied || undefined },
            },
          },
          { headers: getAuthHeaders() }
        );
        const parts = [];
        if (data.unlinked) parts.push(`${data.unlinked} unlinked`);
        if (data.jobsCancelled) parts.push(`${data.jobsCancelled} open job(s) cancelled`);
        setSnack({
          open: true,
          severity: "success",
          text: parts.length ? parts.join(" · ") : data.message || "Done.",
        });
        setSelectionModel([]);
        await loadCustomers();
      } catch (err) {
        setError(err.response?.data?.message || "Action failed.");
      } finally {
        setActionLoading(false);
        setConfirmBulk({ open: false, action: null });
      }
    },
    [connection, jobsFilter, searchApplied, loadCustomers]
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, px: 0.5 }}>
        Customers in your workspace
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5, lineHeight: 1.5 }}>
        Everyone who linked their homeowner account to your business or has at least one job with you. Filter by
        relationship and job activity, then select rows, use <strong>Select all matching</strong> (up to 500), or run an
        action on <strong>all matching filters</strong> without checking rows.
      </Typography>

      <Box
        sx={{
          ...g.panel,
          borderRadius: 3,
          p: 2,
          mb: 2,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" sx={{ mb: 2, alignItems: "flex-start" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="cust-conn-label">Relationship</InputLabel>
            <Select
              labelId="cust-conn-label"
              label="Relationship"
              value={connection}
              onChange={(e) => setConnection(e.target.value)}
            >
              {CUSTOMER_CONNECTION_FILTERS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="cust-jobs-label">Jobs</InputLabel>
            <Select
              labelId="cust-jobs-label"
              label="Jobs"
              value={jobsFilter}
              onChange={(e) => setJobsFilter(e.target.value)}
            >
              {CUSTOMER_JOBS_FILTERS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search name, email, phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexWrap="wrap" sx={{ mb: 2 }} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAllMatching}
            disabled={loading || actionLoading}
          >
            Select all matching (max 500)
          </Button>
          <Button size="small" onClick={() => setSelectionModel([])} disabled={!selectionModel.length}>
            Clear selection ({selectionModel.length})
          </Button>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
            Bulk on checked rows:
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={!selectionModel.length || actionLoading}
            onClick={() => setConfirmBulk({ open: true, action: "unlink_selected" })}
          >
            Unlink dashboard
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={!selectionModel.length || actionLoading}
            onClick={() => setConfirmBulk({ open: true, action: "cancel_selected" })}
          >
            Cancel open jobs
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
            All rows matching filters (max 500):
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={actionLoading || loading}
            onClick={() => setConfirmBulk({ open: true, action: "unlink_filtered" })}
          >
            Unlink all matching
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={actionLoading || loading}
            onClick={() => setConfirmBulk({ open: true, action: "cancel_filtered" })}
          >
            Cancel open jobs (all matching)
          </Button>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            height: 480,
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.6)",
            backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={customerColumns}
            getRowId={(r) => r.id}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectionModel}
            onSelectionModelChange={(m) => setSelectionModel(m)}
            pagination
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            loading={loading}
            sx={{
              border: "none",
              color: "text.primary",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
              },
              "& .MuiDataGrid-cell": {
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              },
            }}
          />
        </Box>
      </Box>

      <Dialog open={confirmBulk.open} onClose={() => !actionLoading && setConfirmBulk({ open: false, action: null })}>
        <DialogTitle>
          {confirmBulk.action?.includes("unlink") ? "Unlink accounts?" : "Cancel open jobs?"}
        </DialogTitle>
        <DialogContent>
          {confirmBulk.action === "unlink_selected" || confirmBulk.action === "unlink_filtered" ? (
            <Typography variant="body2">
              {confirmBulk.action === "unlink_filtered"
                ? "This removes the dashboard link to your business for every customer matching the current filters (up to 500). It does not delete jobs or accounts."
                : `Remove your business link for ${selectionModel.length} selected customer(s). Jobs are kept.`}
            </Typography>
          ) : (
            <Typography variant="body2">
              {confirmBulk.action === "cancel_filtered"
                ? "Cancel all open jobs (pending through in progress) for every customer matching the current filters (up to 500)."
                : `Cancel open jobs for ${selectionModel.length} selected customer(s).`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulk({ open: false, action: null })} disabled={actionLoading}>
            Back
          </Button>
          <Button
            variant="contained"
            color={confirmBulk.action?.includes("unlink") ? "warning" : "error"}
            disabled={actionLoading}
            onClick={() => {
              const a = confirmBulk.action;
              if (a === "unlink_selected") runBulk("unlink");
              else if (a === "cancel_selected") runBulk("cancel_open_jobs");
              else if (a === "unlink_filtered") runBulkFiltered("unlink");
              else if (a === "cancel_filtered") runBulkFiltered("cancel_open_jobs");
            }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function AdminJobsGrid() {
  const g = useAppGlass();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [rows, setRows] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");

  useEffect(() => {
    axios
      .get("/crew", { headers: getAuthHeaders() })
      .then(({ data }) => setCrewList(Array.isArray(data) ? data : []))
      .catch(() => setCrewList([]));
  }, []);

  const handleStatusChange = useCallback(async (rowId, status) => {
    setStatusError("");
    try {
      const { data } = await axios.patch(`/jobs/${rowId}`, { status }, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...data } : r)));
    } catch (err) {
      setStatusError(err.response?.data?.message || "Could not update status.");
    }
  }, []);

  const patchJobRow = useCallback(async (rowId, payload) => {
    setStatusError("");
    try {
      const { data } = await axios.patch(`/jobs/${rowId}`, payload, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...data } : r)));
    } catch (err) {
      setStatusError(err.response?.data?.message || "Could not update job.");
    }
  }, []);

  const geocodeRow = useCallback(async (rowId) => {
    setStatusError("");
    try {
      const { data } = await axios.post(`/jobs/${rowId}/geocode`, {}, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...data } : r)));
    } catch (err) {
      setStatusError(err.response?.data?.message || "Geocode failed.");
    }
  }, []);

  const adminColumns = useMemo(
    () => [
      { field: "customerName", headerName: "Customer", width: 130 },
      { field: "customerEmail", headerName: "Email", flex: 1, minWidth: 140 },
      { field: "serviceName", headerName: "Service", width: 110 },
      { field: "propertyAddress", headerName: "Address", flex: 1, minWidth: 120 },
      {
        field: "scheduledDate",
        headerName: "Scheduled",
        width: 128,
        sortable: false,
        renderCell: (params) => (
          <TextField
            type="date"
            size="small"
            defaultValue={params.row.scheduledDate || ""}
            InputLabelProps={{ shrink: true }}
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (params.row.scheduledDate || "")) {
                patchJobRow(params.row.id, { scheduledDate: v });
              }
            }}
            sx={{ width: 120, my: 0.5 }}
          />
        ),
      },
      {
        field: "scheduledTimeWindow",
        headerName: "Window",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <TextField
            size="small"
            defaultValue={params.row.scheduledTimeWindow || ""}
            placeholder="AM…"
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (params.row.scheduledTimeWindow || "")) {
                patchJobRow(params.row.id, { scheduledTimeWindow: v });
              }
            }}
            sx={{ width: 90, my: 0.5 }}
          />
        ),
      },
      {
        field: "assignedCrewMemberId",
        headerName: "Crew",
        width: 130,
        sortable: false,
        renderCell: (params) => (
          <FormControl size="small" sx={{ my: 0.5, minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
            <Select
              value={params.row.assignedCrewMemberId || ""}
              onChange={(e) =>
                patchJobRow(params.row.id, {
                  assignedCrewMemberId: e.target.value || null,
                })
              }
            >
              <MenuItem value="">—</MenuItem>
              {crewList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.firstname} {c.lastname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      {
        field: "routeOrder",
        headerName: "Rt#",
        width: 70,
        valueFormatter: (p) => (p.value != null ? String(p.value) : "—"),
      },
      {
        field: "geo",
        headerName: "Map",
        width: 72,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Geocode address for routes">
            <IconButton size="small" onClick={() => geocodeRow(params.row.id)} aria-label="Geocode">
              <Typography variant="caption">Pin</Typography>
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "proposal",
        headerName: "Proposal",
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const st = params.row.status;
          if (st === "pending") {
            return (
              <Button
                size="small"
                variant="contained"
                onClick={() => patchJobRow(params.row.id, { sendForCustomerApproval: true })}
              >
                Send to customer
              </Button>
            );
          }
          if (st === "awaiting_customer") {
            return (
              <Stack spacing={0.5} alignItems="flex-start">
                <Chip size="small" label="Awaiting customer" color="warning" variant="outlined" />
                <Button size="small" onClick={() => patchJobRow(params.row.id, { withdrawProposal: true })}>
                  Withdraw
                </Button>
              </Stack>
            );
          }
          return (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 172,
        sortable: false,
        renderCell: (params) => {
          const st = params.row.status;
          const choices = operatorStatusChoices(st);
          if (st === "completed" || st === "cancelled") {
            return <Chip size="small" label={jobStatusLabel(st)} variant="outlined" sx={{ my: 0.5 }} />;
          }
          return (
            <FormControl size="small" fullWidth sx={{ my: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={st}
                onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
                displayEmpty
              >
                <MenuItem value={st}>{jobStatusLabel(st)}</MenuItem>
                {choices.map((c) => (
                  <MenuItem key={c} value={c}>
                    {jobStatusLabel(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Requested",
        width: 100,
        valueFormatter: (params) => formatShortDate(params.value),
      },
    ],
    [handleStatusChange, patchJobRow, geocodeRow, crewList]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get("/jobs", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, px: 0.5 }}>
        All service jobs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5, lineHeight: 1.5 }}>
        Requests arrive as <strong>pending</strong>. Set the visit date, then <strong>Send to customer</strong>. When they
        approve, status becomes <strong>scheduled</strong>. Then mark <strong>in progress</strong> and{" "}
        <strong>completed</strong>.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      {statusError ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setStatusError("")}>
          {statusError}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 440,
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.6)",
            backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={adminColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            sx={{
              border: "none",
              color: "text.primary",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
              },
              "& .MuiDataGrid-cell": {
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              },
            }}
          />
        </Box>
      )}
      <Reports />
    </Box>
  );
}

function Reports() {
  return (
    <Box sx={{ mt: 2, px: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.5 }}>
        <strong>Reports</strong> — summaries and exports are not available yet. This section will appear here when
        billing and job exports ship.
      </Typography>
    </Box>
  );
}
